import React, { useState, useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import DiscoveryPage from "@/pages/DiscoveryPage";
import ContractViewPage from "@/pages/ContractViewPage";
import TemplatesPage from "@/pages/TemplatesPage";
import LegalFeedPage from "@/pages/LegalFeedPage";
import RecommendPage from "@/pages/RecommendPage";
import CustomTemplatesPage from "@/pages/CustomTemplatesPage";
import ExternalSigningPage from "@/pages/ExternalSigningPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "@/pages/SubscriptionCancelPage";
import AdminPage from "@/pages/AdminPage";
import SupportPage from "@/pages/SupportPage";
import ReferralsPage from "@/pages/ReferralsPage";
import DisruptiveToolsPage from "@/pages/DisruptiveToolsPage";
import DocumentVerificationPage from "@/pages/DocumentVerificationPage";
import ApprovalPage from "@/pages/ApprovalPage";
import SmartContractCreationPage from "@/pages/SmartContractCreationPage";
import NegotiationAssistantPage from "@/pages/NegotiationAssistantPage";
import ContractAnalysisPage from "@/pages/ContractAnalysisPage";
import ClauseComparatorPage from "@/pages/ClauseComparatorPage";
import AnalysisHistoryPage from "@/pages/AnalysisHistoryPage";
import ContractVersionsPage from "@/pages/ContractVersionsPage";
import SmartAlertsPage from "@/pages/SmartAlertsPage";
import HealthMonitorPage from "@/pages/HealthMonitorPage";
import DirectTemplatePage from "@/pages/DirectTemplatePage";
import AccountingAIPage from "@/pages/AccountingAIPage";
import LegalDeadlinesPage from "@/pages/LegalDeadlinesPage";
import LaborCalculatorPage from "@/pages/LaborCalculatorPage";
import DeadlinesCalendarPage from "@/pages/DeadlinesCalendarPage";
import FamilyHubPage from "@/pages/FamilyHubPage";
import SpecialistAgentsPage from "@/pages/SpecialistAgentsPage";
import ProfilePage from "@/pages/ProfilePage";
import { PayChargePage, PayPalSubscriptionReturnPage } from "@/pages/PayPalPages";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordResetPages";
import JurisprudencePage from "@/pages/JurisprudencePage";
import LegalDocumentsPageEnhanced from "@/pages/LegalDocumentsPageEnhanced";
import PoderJudicialPage from "@/pages/PoderJudicialPage";
import ElectronicSignaturePage from "@/pages/ElectronicSignaturePage";
import ContractProtectionPage from "@/pages/ContractProtectionPage";
import ContractComparisonPage from "@/pages/ContractComparisonPage";
import ProtectionHistoryPage from "@/pages/ProtectionHistoryPage";
import CompanyConstitutionPage from "@/pages/CompanyConstitutionPage";
import LegalTriagePage from "@/pages/LegalTriagePage";
import JudicialIntelligencePage from "@/pages/JudicialIntelligencePage";
import CRMPage from "@/pages/CRMPage";
import JudgePage from "@/pages/JudgePage";
import TrialSimPage from "@/pages/TrialSimPage";
import CourtsPage from "@/pages/CourtsPage";
import FidemPage from "@/pages/FidemPage";
import FidemConnectionsPage from "@/pages/FidemConnectionsPage";
import FidemMessagesPage from "@/pages/FidemMessagesPage";
import FidemClassifyPage from "@/pages/FidemClassifyPage";
import VocabularyPage from "@/pages/VocabularyPage";
import { GoogleAuthCallback } from './components/auth/GoogleAuthCallback';
import WatchedLawsPage from "@/pages/WatchedLawsPage";
import AreasCourtsPage from "@/pages/AreasCourtsPage";
import { ActiveMatterBar } from "@/components/ActiveMatterBar";
import ExpertsPage from "@/pages/ExpertsPage";
import SecurityPage from "@/pages/SecurityPage";
import TrustCenterPage from "@/pages/TrustCenterPage";
import { MfaBanner } from "@/components/security/MfaBanner";
import { LangProvider } from "@/i18n/LangContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

// Legal Pages
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";
import CookiePolicyPage from "@/pages/legal/CookiePolicyPage";
import LegalNoticePage from "@/pages/legal/LegalNoticePage";

// Components
import MobileNavigation from "@/components/MobileNavigation";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { OfflineToast } from "@/components/OfflineIndicator";
import { useServiceWorker } from "@/hooks/usePWA";
import OnboardingTour from "@/components/OnboardingTour";
import UnifiedAIAssistant from "@/components/UnifiedAIAssistant";
import LegalWritingAssistant from "@/components/LegalWritingAssistant";
import CookieConsent from "@/components/CookieConsent";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center mesh-gradient">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  if (user.mfa?.setup_required && location.pathname !== "/seguridad") {
    return <Navigate to="/seguridad" replace />;
  }
  
  return <>{user.mfa?.required && !user.mfa?.enabled && <MfaBanner graceUntil={user.mfa.grace_until} />}{children}</>;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const location = useLocation();

  // Check if user is new and should see onboarding
  useEffect(() => {
    if (user && location.pathname === '/dashboard') {
      const tourCompleted = localStorage.getItem('notaryos_tour_completed');
      const isNewUser = !tourCompleted;
      if (isNewUser) {
        // Never interrupt an active payment/review dialog; try on a later visit instead.
        const timer = setTimeout(() => {
          if (!document.querySelector('[role="dialog"], [data-testid="subscription-plans-dialog"]')) setShowOnboarding(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, location.pathname]);

  if (new URLSearchParams(location.hash.slice(1)).has('session_id')) {
    return <GoogleAuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center mesh-gradient">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      {/* Onboarding Tour for new users */}
      {showOnboarding && (
        <OnboardingTour onComplete={() => setShowOnboarding(false)} />
      )}
      
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/discovery" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
        <Route path="/discovery/:templateId" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
        <Route path="/contract/:contractId" element={<ProtectedRoute><ContractViewPage /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
        <Route path="/my-templates" element={<ProtectedRoute><CustomTemplatesPage /></ProtectedRoute>} />
        <Route path="/recommend" element={<ProtectedRoute><RecommendPage /></ProtectedRoute>} />
        <Route path="/legal-feed" element={<ProtectedRoute><LegalFeedPage /></ProtectedRoute>} />
        <Route path="/sign/:contractId/:token" element={<ExternalSigningPage />} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
        <Route path="/subscription/cancel" element={<ProtectedRoute><SubscriptionCancelPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
        <Route path="/tools" element={<ProtectedRoute><DisruptiveToolsPage /></ProtectedRoute>} />
        <Route path="/crear-contrato" element={<ProtectedRoute><SmartContractCreationPage /></ProtectedRoute>} />
        <Route path="/crear-contrato/:templateId" element={<ProtectedRoute><SmartContractCreationPage /></ProtectedRoute>} />
        <Route path="/negociacion" element={<ProtectedRoute><NegotiationAssistantPage /></ProtectedRoute>} />
        <Route path="/analizar-contrato" element={<ProtectedRoute><ContractAnalysisPage /></ProtectedRoute>} />
        <Route path="/comparador-clausulas" element={<ProtectedRoute><ClauseComparatorPage /></ProtectedRoute>} />
        <Route path="/historial-analisis" element={<ProtectedRoute><AnalysisHistoryPage /></ProtectedRoute>} />
        <Route path="/versiones/:contractId" element={<ProtectedRoute><ContractVersionsPage /></ProtectedRoute>} />
        <Route path="/alertas" element={<ProtectedRoute><SmartAlertsPage /></ProtectedRoute>} />
        <Route path="/admin/health" element={<ProtectedRoute><HealthMonitorPage /></ProtectedRoute>} />
        <Route path="/plantilla/:templateId" element={<ProtectedRoute><DirectTemplatePage /></ProtectedRoute>} />
        <Route path="/contabilidad" element={<ProtectedRoute><AccountingAIPage /></ProtectedRoute>} />
        <Route path="/plazos-legales" element={<ProtectedRoute><LegalDeadlinesPage /></ProtectedRoute>} />
        <Route path="/calculadora-laboral" element={<ProtectedRoute><LaborCalculatorPage /></ProtectedRoute>} />
        <Route path="/hub-familia" element={<ProtectedRoute><FamilyHubPage /></ProtectedRoute>} />
        <Route path="/agentes-especialistas" element={<ProtectedRoute><SpecialistAgentsPage /></ProtectedRoute>} />
            <Route path="/fidem" element={<ProtectedRoute><FidemPage /></ProtectedRoute>} />
            <Route path="/fidem/conexiones" element={<ProtectedRoute><FidemConnectionsPage /></ProtectedRoute>} />
            <Route path="/fidem/mensajes" element={<ProtectedRoute><FidemMessagesPage /></ProtectedRoute>} />
            <Route path="/fidem/clasificar" element={<ProtectedRoute><FidemClassifyPage /></ProtectedRoute>} />
            <Route path="/vocabulario-juridico/*" element={<ProtectedRoute><VocabularyPage /></ProtectedRoute>} />
        <Route path="/leyes-vigiladas" element={<ProtectedRoute><WatchedLawsPage /></ProtectedRoute>} />
        <Route path="/areas-y-tribunales" element={<ProtectedRoute><AreasCourtsPage /></ProtectedRoute>} />
        <Route path="/cortes" element={<ProtectedRoute><CourtsPage /></ProtectedRoute>} />
        <Route path="/simulaciones" element={<ProtectedRoute><TrialSimPage /></ProtectedRoute>} />
        <Route path="/simulaciones/:sid" element={<ProtectedRoute><TrialSimPage /></ProtectedRoute>} />
        <Route path="/calendario-plazos" element={<ProtectedRoute><DeadlinesCalendarPage /></ProtectedRoute>} />
        <Route path="/pagar/:token" element={<PayChargePage />} />
        <Route path="/suscripcion/paypal" element={<ProtectedRoute><PayPalSubscriptionReturnPage /></ProtectedRoute>} />
        <Route path="/recuperar-clave" element={<ForgotPasswordPage />} />
        <Route path="/restablecer-clave" element={<ResetPasswordPage />} />
        <Route path="/mi-perfil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/seguridad" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
        <Route path="/confianza" element={<TrustCenterPage />} />
        <Route path="/jurisprudencia" element={<ProtectedRoute><JurisprudencePage /></ProtectedRoute>} />
        <Route path="/escritos-legales" element={<ProtectedRoute><LegalDocumentsPageEnhanced /></ProtectedRoute>} />
        <Route path="/poder-judicial" element={<ProtectedRoute><PoderJudicialPage /></ProtectedRoute>} />
        <Route path="/firma-electronica" element={<ProtectedRoute><ElectronicSignaturePage /></ProtectedRoute>} />
        <Route path="/proteger-contrato" element={<ProtectedRoute><ContractProtectionPage /></ProtectedRoute>} />
        <Route path="/comparar-contratos" element={<ProtectedRoute><ContractComparisonPage /></ProtectedRoute>} />
        <Route path="/historial-proteccion" element={<ProtectedRoute><ProtectionHistoryPage /></ProtectedRoute>} />
        <Route path="/constituir-empresa" element={<ProtectedRoute><CompanyConstitutionPage /></ProtectedRoute>} />
        <Route path="/consejero-legal" element={<ProtectedRoute><LegalTriagePage /></ProtectedRoute>} />
        <Route path="/inteligencia-judicial" element={<ProtectedRoute><JudicialIntelligencePage /></ProtectedRoute>} />
        <Route path="/expertos-notariales" element={<ProtectedRoute><ExpertsPage /></ProtectedRoute>} />
        <Route path="/crm" element={<ProtectedRoute><CRMPage /></ProtectedRoute>} />
        <Route path="/jueces/:judgeId" element={<ProtectedRoute><JudgePage /></ProtectedRoute>} />
        <Route path="/judicial-intelligence" element={<ProtectedRoute><JudicialIntelligencePage /></ProtectedRoute>} />
        <Route path="/triage-legal" element={<ProtectedRoute><LegalTriagePage /></ProtectedRoute>} />
        <Route path="/verificar" element={<DocumentVerificationPage />} />
        <Route path="/verify" element={<DocumentVerificationPage />} />
        <Route path="/aprobacion/:token" element={<ApprovalPage />} />
        {/* Legal Pages - Public */}
        <Route path="/legal/privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/legal/terminos" element={<TermsOfServicePage />} />
        <Route path="/legal/cookies" element={<CookiePolicyPage />} />
        <Route path="/legal/aviso-legal" element={<LegalNoticePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  // Initialize service worker
  useServiceWorker();

  return (
    <ThemeProvider>
      <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: 'hsl(222 47% 15%)',
                color: 'hsl(210 40% 98%)',
                border: '1px solid hsl(217 33% 27%)'
              }
            }}
          />
          <div className="pb-16 md:pb-0">
            <AppRoutes />
          </div>
          <AppChrome />
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}

// Internal widgets; hidden on client-facing public pages (brief approval)
function AppChrome() {
  const location = useLocation();
  if (new URLSearchParams(location.hash.slice(1)).has('session_id')) return null;
  if (location.pathname.startsWith("/aprobacion/")) return null;
  const readingMode = location.pathname.startsWith('/vocabulario-juridico');
  return (
    <>
      {!readingMode && <ActiveMatterBar />}
      {!readingMode && <MobileNavigation />}
      <PWAInstallPrompt />
      <OfflineToast />
      {!readingMode && <LegalWritingAssistant />}
      <UnifiedAIAssistant readingMode={readingMode} />
      {!readingMode && <div className="fixed bottom-[4.6rem] left-1/2 z-40 -translate-x-1/2 md:bottom-4 md:left-3 md:translate-x-0"><LanguageSwitcher /></div>}
    </>
  );
}

export default App;
